import { useState } from "react";
import Image from "react-bootstrap/Image";
import Button from "react-bootstrap/Button";
import { RotateCcw } from "lucide-react";
import enquirySuccess from "../assets/images/enquiry-success.png";
import "../assets/css/contact.css";
import { Row, Col } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import LayerFour from "../assets/images/layers/Layer_4.png";
import { apiUrl, apiHeaders } from "../apiConfig";

const DataReadinessModal = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const emptyForm = {
    name: "",
    email: "",
    company: "",
    heardAbout: "",
    jobTitle: "",
    mobile: "",
    otherChallenge: "", // For other data challenge input
  };
  const [formData, setFormData] = useState(emptyForm);

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Email validation regex (blocks free providers)
  const businessEmailRegex =
    /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|aol\.com|icloud\.com|protonmail\.com|zoho\.com|mail\.com|gmx\.com)$/i;

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name?.trim()) {
      newErrors.name = "Full Name is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Business Email is required";
    } else if (businessEmailRegex.test(formData.email)) {
      newErrors.email = "Please use a business email address";
    }

    if (!formData.company?.trim()) {
      newErrors.company = "Company Name is required";
    }

    if (!formData.heardAbout) {
      newErrors.heardAbout = "Please select your key data challenge";
    } else if (
      formData.heardAbout === "Other" &&
      !formData.otherChallenge?.trim()
    ) {
      newErrors.otherChallenge = "Please specify your data challenge";
    }

    // Phone number format validation (optional field)
    const phoneNumber = formData.mobile || "";
    if (phoneNumber && !/^\d{10}$/.test(phoneNumber.replace(/[^\d]/g, ""))) {
      newErrors.mobile = "Please enter a valid 10-digit phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");
    setErrors({});

    // Validate form
    if (!validateForm()) {
      setLoading(false);
      setResponseMessage("Please correct the errors in the form.");
      return;
    }

    try {
      // The server builds and sends the emails; SMTP credentials stay server-side
      const res = await fetch(apiUrl("/api/data-readiness"), {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          jobTitle: formData.jobTitle,
          mobile: formData.mobile,
          heardAbout: formData.heardAbout,
          otherChallenge: formData.otherChallenge,
        }),
      });
      const response = await res.json().catch(() => null);
      if (!response) {
        // Non-JSON reply: the API server is down or restarting
        setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
      } else if (res.ok && response.success) {
        setIsSubmitted(true);
        setFormData(emptyForm);
      } else {
        setResponseMessage(
          response.message || "Failed to send email. Please try again."
        );
      }
    } catch (error) {
      setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="bg-E2F1FE position-relative box-shadow-one rounded-4 pt-5 p-23 p-sm-23">
        <Image
          src={LayerFour}
          className="position-absolute bottom-0 start-0 w-40"
        />

        <Row className="position-relative form-box z-0">
          {isSubmitted ? null : (
            <Col lg={5}>
              <div class="text-left d-flex  justify-content-start">
                <h2 class="section-heading fw-700 fs-2">
                  Get Your Free Data{" "}
                  <span className="bordered-text bordered-text-start">
                    Readiness{" "}
                  </span>
                  <span className="text-ee0000"> Assessment</span>
                </h2>
              </div>
            </Col>
          )}
          <Col lg={isSubmitted ? 12 : 7}>
            {isSubmitted ? (
              <div
                className="enquiry-success"
                role="status"
                aria-live="polite"
              >
                <Image src={enquirySuccess} alt="" className="enquiry-success-img" />
                <h3 className="enquiry-success-title">Thank you</h3>
                <p className="enquiry-success-subtitle">
                  We&rsquo;ve received your request.
                </p>
                <p className="enquiry-success-text">
                  Our data experts will review your details and get in touch
                  with you soon to schedule your Free Data Readiness
                  Assessment. A confirmation has been sent to your inbox.
                </p>
                <div className="enquiry-success-actions">
                  <button
                    type="button"
                    className="enquiry-success-link"
                    onClick={() => setIsSubmitted(false)}
                  >
                    <RotateCcw size={15} /> Submit another request
                  </button>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row className="pb-3 mt-3 mt-lg-0">
                  <Col lg={6}>
                    <>
                      <Form.Label htmlFor="inputPassword5">
                        Full Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </>
                  </Col>
                  <Col lg={6}>
                    <>
                      <Form.Label>
                        Business Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </>
                  </Col>
                  <Col lg={6}>
                    <>
                      <Form.Label htmlFor="CompanyName">
                        Company Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        isInvalid={!!errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Job Title/Role{" "}
                        <span className="text-muted">(Optional)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Number </Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Enter 10-digit phone number"
                        name="mobile"
                        value={formData.mobile}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 10);
                          setFormData({ ...formData, mobile: value });
                        }}
                        isInvalid={!!errors.mobile}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.mobile && (
                        <Form.Control.Feedback type="invalid">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        What's your key data challenge?{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="heardAbout"
                        value={formData.heardAbout}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.heardAbout}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="Data quality issues">
                          Data quality issues
                        </option>
                        <option value="Lack of integration">
                          Lack of integration
                        </option>
                        <option value="Security/compliance concerns">
                          Security/compliance concerns
                        </option>
                        <option value="Limited insights/analytics">
                          Limited insights/analytics
                        </option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {formData.heardAbout === "Other" && (
                        <Form.Control
                          type="text"
                          name="otherChallenge"
                          placeholder="Please specify your data challenge"
                          value={formData.otherChallenge}
                          onChange={handleChange}
                          className="rounded-1 custom-input custom-input-two mt-2"
                        />
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={12} className="mt-4 text-start">
                    <>
                      <Button
                        variant=""
                        type="submit"
                        className="fw-600 d-block btn btn-danger btn-xl"
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </>
                  </Col>
                  {responseMessage && (
                    <p
                      className="mt-3 mb-0 fs-12 text-center"
                      style={{ color: loading ? "blue" : "red" }}
                    >
                      {responseMessage}
                    </p>
                  )}
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default DataReadinessModal;
